import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const comic = comics.find(c => c.id === id);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter(c => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 免费韩国漫画在线阅读 - 奇漫屋`}
        description={`${comic.title}，${comic.author}创作的${comic.category}类韩国漫画。${comic.description} 奇漫屋提供免费在线下拉式阅读，画质清晰。`}
        keywords={`${comic.title}, ${comic.author}, ${comic.category}韩漫, 韩国漫画, 在线漫画, 奇漫屋`}
        canonicalUrl={`https://qimanwu.com/article/${comic.id}`}
        ogType="article"
      />
      <SchemaOrg
        type="WebPage"
        pageUrl={`https://qimanwu.com/article/${comic.id}`}
        pageName={`${comic.title} - 奇漫屋`}
      />
      <SchemaOrg
        type="BreadcrumbList"
        breadcrumbItems={[
          { name: '首页', url: 'https://qimanwu.com' },
          { name: `${comic.category}韩漫`, url: `https://qimanwu.com/category/${comic.categorySlug}` },
          { name: comic.title, url: `https://qimanwu.com/article/${comic.id}` },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb />

          <article className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full lg:w-[300px]">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200 sticky top-24">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 免费韩国漫画在线阅读`}
                    title={`${comic.title} - ${comic.category}韩漫`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 cursor-pointer hover:bg-accent-200 transition-colors"
                  >
                    {comic.category}
                  </Link>
                </div>

                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">
                  作者：<strong>{comic.author}</strong> · 更新于 {comic.updatedAt} · 发布于 {comic.publishDate}
                </p>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill"></i>
                    </span>
                    <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                      <i className="ri-eye-line"></i>
                    </span>
                    <span className="text-sm font-semibold text-foreground-800">
                      {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                    </span>
                    <span className="text-xs text-foreground-400">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                      <i className="ri-file-list-3-line"></i>
                    </span>
                    <span className="text-sm font-semibold text-foreground-800">{comic.chapters.length}</span>
                    <span className="text-xs text-foreground-400">章节</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-8">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.longDescription}
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    章节列表 ({comic.chapters.length}话)
                  </h2>
                  <div className="space-y-2">
                    {comic.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg cursor-pointer hover:border-primary-300 hover:bg-primary-50/30 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                            <i className="ri-book-read-line"></i>
                          </span>
                          <span className="text-sm font-medium text-foreground-800">
                            {chapter.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {chapter.updatedAt}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
                  {relatedComics.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="group block cursor-pointer"
                      data-product-shop
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                        <img
                          src={related.cover}
                          alt={`${related.title} - 韩国漫画`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {related.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}